// Serves reservation wizard and team request

import { Injectable, Inject } from '@angular/core';
import { AvailabilityInput, AvailabilityType, CreateReservationResponse, EndAfterType, EventTime, Features, FloorBookingInput, FloorFilter, LocationItem, MatchType, OrderType, RecurrencePatternTypes, ReservationDetailsFormValue, ReservationInput, ReservationType, SpaceAvailability, SpaceAvailabilityItem, SpaceTypeCategory, SpaceView, TeamRequestBookingInput, TimeSlotType, UnfulfilledRequest, WEEK_DAYS } from 'src/app/models/find-a-space.model';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { DataContextService, XUseSpaceEnum } from '../../datacontext.service';
import * as moment from 'moment-timezone';
import { BookingDetails, ConflictingSpaceBooking, ReservationVerifyType, ResolveConflictsChoise, WaitListCreationRequest } from 'src/app/models/bookings.model';
import { SPACE_FEATURES_ICON } from '../../user-settings.service';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { AmenitiesCacheData, FloorPlan } from '../../../models/find-a-space.model';
import { TeamRequest, TeamRequestAvailability, TeamRequestAvailabilityInput, TeamRequestBase, TeamRequestDraft, TeamRequestInput } from '../../../models/find-a-space.model';
import { UserService } from '../Users/userservice';
import { UserByEmail } from '../../../models/users.model';
import { Amenity, Floor, FloorAmenity, Space, TimeZone } from '../../../models/masterdata.model';
import { BookingsService } from '../Bookings/bookings.service';
import { DateHelper } from 'src/app/shared/Helper/dateHelper';
import { SpinnerService } from 'src/app/common/spinner/spinner.service';
import { ToasterService, ToasterType } from 'src/app/common/toaster/toaster.service';

@Injectable({
  providedIn: 'root'
})
export class FindASpaceService {

  constructor(private dataContextService: DataContextService,
              private userService: UserService,
              private bookingsService: BookingsService,
              @Inject(SPACE_FEATURES_ICON) private spaceFeaturesIconMap: string,
              private spinnerService: SpinnerService) { }



  private get isCacheExpired(): boolean {
    if (this.amenityCacheData && !this.dataContextService.isCacheExpired(this.amenityCacheData.LastUpdated, 'AmenityExpirationTime')) {
      return false;
    }
    this.amenityCacheData = {
      Amenities: [],
      LastUpdated: null
    };
    return true;
  }
  amenityCacheData: AmenitiesCacheData;
  refreshTeamRequestDrafts: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  unfulfilledTeamRequest$: BehaviorSubject<UnfulfilledRequest> = new BehaviorSubject(null);
  private _floorAmenities = new BehaviorSubject<FloorAmenity[]>(null);
  floorAmenities$ = this._floorAmenities.asObservable();

  private _appliedFloorFilters = new BehaviorSubject<FloorFilter>(null);
  appliedFloorFilters$ = this._appliedFloorFilters.asObservable().pipe(shareReplay(1));

  availabilitySearchParameter: Partial<AvailabilityInput>;

  setFloorAmenities(amenities: FloorAmenity[]): void {
    this._floorAmenities.next(amenities);
  }

  applyFloorFilters(filters: FloorFilter): void {
    this._appliedFloorFilters.next(filters);
  }

  getFilters(): FloorFilter {
    return this._appliedFloorFilters.getValue();
  }


  getTeamRequestDrafts(): Observable<TeamRequestDraft[]> {
    const url = `TeamRequestDrafts`;
    return this.dataContextService.getData(url).pipe(
      map(res => res.data as TeamRequestDraft[])
    );
  }
  createTeamRequestDraft(payload: TeamRequestBase) {
    const url = `TeamRequestDrafts`;
    return this.dataContextService.postData(url, payload).pipe(
      map(res => res.data)
    );
  }
  deleteTeamRequestDraft(teamName: string) {
    const url = `TeamRequestDrafts/${teamName}`;
    return this.dataContextService.deleteData(url).pipe(
      map(res => res.data)
    );
  }
  confirmTeamRequest(teamRequestId: string) {
    const url = `TeamRequestItems/${teamRequestId}/Confirm`;
    return this.dataContextService.patchData(url, {}).pipe(
      map(res => res.data)
    );
  }
  getTeamRequest(input: TeamRequestInput): Observable<TeamRequest[]> {
    let url = `TeamRequests?`;
    if (input.term) {
      url += `Term=${input.term}&`;
    }
    if (input.teamName) {
      url += `TeamName=${input.teamName}&`;
    }
    if (input.buildingIds && input.buildingIds.length) {
      input.buildingIds.forEach((id) => {
        url += `BuildingIds=${id}&`;
      });
    }
    if (input.reservationId) {
      url += `ReservationId=${input.reservationId}&`;
    }
    return this.dataContextService.getData(url, false).pipe(
      map(res => res.data as TeamRequest[])
    );
  }
  createTeamRequest(payload: TeamRequestBase) {
    const url = `TeamRequests/Publish`;
    return this.dataContextService.postData(url, payload).pipe(
      map(res => res.data)
    );
  }
  getTeamRequestAvailability(payload: TeamRequestAvailabilityInput): Observable<TeamRequestAvailability[]> {
    const url = `TeamSpaces/Availability`;
    return this.dataContextService.postData(url, payload).pipe(
      map(res => res.data && res.data.floors as TeamRequestAvailability[])
    );
  }

  // create paylod for RW availability call
  availableSpacesRW(userId: number, reservationCard: ReservationDetailsFormValue, bookingsCard: LocationItem, mapView: boolean = false, availabiltyTypes: string[]): Observable<SpaceAvailability> {
    const isDelegate = DataContextService.UserData && DataContextService.UserData.id !== userId && reservationCard.reservationDetails.allocation == XUseSpaceEnum[XUseSpaceEnum.Delegate];
    const tzOffset = moment.tz(new Date(), bookingsCard.building.timeZoneIanaName).format('Z');

    const locationStartDate = `${moment(bookingsCard.startDate).format('YYYY-MM-DD')}T${moment(bookingsCard.startTime).format('HH:mm:00')}${tzOffset}`;
    const locationEndDate = `${moment(bookingsCard.endDate).format('YYYY-MM-DD')}T${moment(bookingsCard.endTime).format('HH:mm:00')}${tzOffset}`;

    let eventTime: EventTime;
    if (reservationCard.reservationDetails.searchType == ReservationType.Standard) {
      eventTime = {
        timeSlotType: TimeSlotType.SingleDay,
        singleDayEventData: {
          startDate: locationStartDate,
          endDate: locationEndDate,
        }
      };
    }

    else if (reservationCard.reservationDetails.searchType == ReservationType.MultiDayRecurring) {
      eventTime = {
        timeSlotType: TimeSlotType.Recurrent,
        recurrentEventData: {
          recurrenceRule: {
            recurrencePatternType: RecurrencePatternTypes[reservationCard.reservationDetails.recurring.repeatEvery.pattern],
            interval: reservationCard.reservationDetails.recurring.repeatEvery.pattern === RecurrencePatternTypes.Daily ? reservationCard.reservationDetails.recurring.repeatEvery.days : reservationCard.reservationDetails.recurring.repeatEvery.weeks,
            startDate: locationStartDate,
            endDate: locationEndDate,
            weeklyRecurrencePattern: reservationCard.reservationDetails.recurring.repeatEvery.pattern === RecurrencePatternTypes.Weekly ? { daysOfWeek: WEEK_DAYS.filter((_, i) => reservationCard.reservationDetails.recurring.repeatEvery.weekDays[i]).map((d) => d.fullName) } : null
          }
        }
      };
    }

    else {
      eventTime = {
        singleDayEventData: {
          isLtr: true,
          startDate: locationStartDate,
          endDate: `${moment(bookingsCard.startDate).format('YYYY-MM-DD')}T${moment(bookingsCard.endTime).format('HH:mm:00')}${tzOffset}`
        }
      };
    }

    let includeUnavailable = false;
    if (availabiltyTypes != null && availabiltyTypes.length > 0) {
      if (availabiltyTypes.find((item) => item == MatchType.Partial)) {
        includeUnavailable = true;
      }
    }

    if (bookingsCard.spaceType === 'MeetingRoom') {
      eventTime.spaceSetupTime = DateHelper.minutesToTimeString(bookingsCard.setup);
      eventTime.spaceTearDownTime = DateHelper.minutesToTimeString(bookingsCard.teardown);
    }

    let params: Partial<AvailabilityInput> = {
      buildingId: bookingsCard.building.id,
      attendeesCount: bookingsCard.attendees,
      categoryType: this.dataContextService.checkMeetingRoom(bookingsCard.spaceType) ? SpaceTypeCategory.Meeting : SpaceTypeCategory.Individual,
      searchText: bookingsCard.spaceName && bookingsCard.spaceName.length >= 3 ? bookingsCard.spaceName : '',
      orderType: OrderType.Search,
      features: `${Features.TimeSlots},${Features.ScopedSpaces}`,
      includeUnavailable,
      includeInaccessibleToUser: mapView,
      eventTime
    };

    const teamBookings: Observable<BookingDetails[]> = reservationCard.teamRequestId ? this.bookingsService.teamRequestBookings(params, reservationCard.teamRequestId) : of([]);
    let bookingsBySpaceId: { [key: string]: (BookingDetails & { isAttendee?: boolean, isOwner?: boolean, })[] } = {};

    return teamBookings.pipe(switchMap(res => {
      const teamBuildingBookings = res.filter((booking) => booking.space.buildingId == bookingsCard.building.id);
      if (teamBuildingBookings.length) {
        params = { ...params, floorUniqueKey: teamBuildingBookings[0].space.floorUniqueKey, preferredFloorUniqueKey: teamBuildingBookings[0].space.floorUniqueKey };
      }

      bookingsBySpaceId = (teamBuildingBookings || []).reduce((results, booking) => {
        (results[booking.space.id] = results[booking.space.id] || []).push(booking);
        return results;
      }, {});

      teamBuildingBookings.forEach((booking) => {
        if (bookingsBySpaceId[booking.space.id]) {
          bookingsBySpaceId[booking.space.id].push(booking);
        }
        else {
          bookingsBySpaceId[booking.space.id] = [booking];
        }
      });
      this.availabilitySearchParameter = params;
      return this.getSpaces(params, userId, isDelegate).pipe(map(res => {

        res.items.map(item => {
          const { space, suggestedAvailableTimeSlot } = item;

          item.space.category = this.dataContextService.checkMeetingRoom(space.spaceType.type) || this.dataContextService.checkOpenSeating(space.spaceType.type) ? SpaceTypeCategory.Meeting : SpaceTypeCategory.Individual;
          item.isBookingAvailable = item.availabilityType === AvailabilityType.Available || item.availabilityType === AvailabilityType.PartiallyAvailable;

          if (suggestedAvailableTimeSlot) {
            item.suggestedAvailableTimeSlot = {
              startDate: moment(moment.tz(suggestedAvailableTimeSlot.startDate, bookingsCard.timeZone.TZIanaName).format('YYYY-MM-DDTHH:mm:00')).format(),
              endDate: moment(moment.tz(suggestedAvailableTimeSlot.endDate, bookingsCard.timeZone.TZIanaName).format('YYYY-MM-DDTHH:mm:00')).format()
            };
          }

          if (space.features && space.features.length > 0) {
            space.features = space.features.filter(feature => feature.id !== 70).map(feature => {
              feature.iconClass = this.spaceFeaturesIconMap[feature.iconKey];
              return feature;
            });
          }

          if (bookingsBySpaceId && bookingsBySpaceId[space.id]) {
            item.teamRequest = true;
            item.userBookings = bookingsBySpaceId[space.id];
          }

        });
        return teamBuildingBookings.length > 0 ? { ...res, teamRequestFloor: teamBuildingBookings[0].space.floorUniqueKey } : res;
      }),
        // check on switch map usage
        switchMap((res: SpaceAvailability): Observable<SpaceAvailability> => {
          return mapView && res ? this.addFloorBookingsToResponse(res, userId, params, bookingsCard) : of(res);
        })
      );
    }));
  }
  getSpaces(payload: Partial<AvailabilityInput>, userId: number, isDelegate: boolean): Observable<SpaceAvailability> {
    const input = this.dataContextService.removeNullValuesFromObject(payload);
    return this.dataContextService.postData(`Users/${userId}/Spaces/InBuildingAvailability`, input, true, false, isDelegate).pipe(map((res) => {
      if (res.status === 200) {
        return res.data as SpaceAvailability;
      }
    }));
  }
  addFloorBookingsToResponse(spaceAvailability: SpaceAvailability, userId: number, params: Partial<AvailabilityInput>, bookingsCard: LocationItem): Observable<SpaceAvailability> {
    const input = { userId } as FloorBookingInput;

    if (bookingsCard.floor != -1) { // -1 is for any floor
      input.floorUniqueKey = bookingsCard.floor;
    }
    else {
      // in case of any floor selected
      if (spaceAvailability.items && spaceAvailability.items.length > 0) {
        const spaceItem = spaceAvailability.items.find(i => i.availabilityType === AvailabilityType.Available && !i.inaccessibleToUser);
        input.floorUniqueKey = spaceItem ? spaceItem.space.floorUniqueKey : null;
      }
    }

    if (input.floorUniqueKey) {
      if (params.eventTime.timeSlotType === TimeSlotType.Recurrent) {
        input.startDate = this.getDateStringForRequest(params.eventTime.recurrentEventData.recurrenceRule.startDate);
        input.endDate = this.getDateStringForRequest(params.eventTime.recurrentEventData.recurrenceRule.endDate, true);

      } else {
        input.startDate = this.getDateStringForRequest(params.eventTime.singleDayEventData.startDate);
        input.endDate = this.getDateStringForRequest(params.eventTime.singleDayEventData.endDate, true);
      }
    }
    return input.floorUniqueKey ? this.bookingsService.getFloorBookings(input).pipe(map(res => {
      const bookings = res;
      // need to check owner id and attendees logic
      if (!!bookings.length) {
        const bookingsBySpaceId: { [key: string]: (BookingDetails & { isAttendee: boolean, isOwner: boolean })[] } = bookings
          .map(booking => ({
            ...booking,
            isAttendee: booking.owner.id !== userId,
            isOwner: booking.owner.id === userId,
          }))
          .reduce((results, booking) => {
            (results[booking.space.id] = results[booking.space.id] || []).push(booking);
            return results;
          }, {});

        spaceAvailability.items.forEach(item => {
          const userBookings = bookingsBySpaceId[item.space.id] || [];
          item.userBookings = item.userBookings ? item.userBookings.concat(userBookings) : userBookings;
          delete bookingsBySpaceId[item.space.id];
        });

        const otherSpaces = Object.keys(bookingsBySpaceId)
          .filter(id => !!bookingsBySpaceId[id] && !!bookingsBySpaceId[id].length)
          .map(Id => {
            const {
              buildingDisplayName,
              buildingId,
              name,
              spaceType,
              floorName,
              floorUniqueKey,
              id,
              // tslint:disable-next-line:no-shadowed-variable
              centerCoordinates,
              // tslint:disable-next-line:no-shadowed-variable
              sizeCoordinates,
              capacity } = bookingsBySpaceId[Id][0].space;
            return {
              space: {
                buildingDisplayName,
                buildingId,
                capacity,
                centerCoordinates: {
                  x: centerCoordinates.x,
                  y: centerCoordinates.y,
                  pointSize: centerCoordinates.pointSize,
                },
                features: [],
                floorName,
                floorUniqueKey,
                id,
                name,
                scopedSpaceIds: [],
                setupTime: '',
                sizeCoordinates: {
                  height: sizeCoordinates.height,
                  left: sizeCoordinates.left,
                  top: sizeCoordinates.top,
                  width: sizeCoordinates.width,
                },
                spaceType: {
                  id: spaceType.id,
                  type: spaceType.type,
                  name: spaceType.name,
                },
                category: this.dataContextService.checkPrivateSpace(spaceType.type) || this.dataContextService.checkWorkstation(spaceType.type) ? SpaceTypeCategory.Individual : SpaceTypeCategory.Meeting
              } as Space,
              userBookings: bookingsBySpaceId[id],
              isOtherCategory: true,
            } as SpaceAvailabilityItem;
          });
        spaceAvailability.items = spaceAvailability.items.concat(otherSpaces);
      }
      return spaceAvailability;
    }
    )) : of(spaceAvailability);
  }

  private getDateStringForRequest(date: string, endOfDay: boolean = false): string {
    const newDate = new Date(DateHelper.RemoveOffsetFromDate(date));
    if (endOfDay) {
      newDate.setHours(23, 59, 0);
    } else {
      newDate.setHours(0, 0, 0);
    }
    return moment(newDate.getTime()).format('YYYY-MM-DDTHH:mm:00');
  }




  create(task: ReservationTask): Observable<any> {
    return this.dataContextService.postData('Tasks/Reservations', task, true).pipe(map((res) => {
      this.spinnerService.stop();
      return res.status === 200 ? res : ''; // this.dataContextService.showErrorToaster(res);
    }));
  }

  createReservation(userId: number, reservationInput: ReservationInput, isConciergeAllocation: boolean = true) {
    const url = `Users/${userId}/Reservations`;
    const isDelegate = DataContextService.UserData && userId != DataContextService.UserData.id && isConciergeAllocation;
    if (reservationInput.conflictingSpaceBookings && reservationInput.conflictingSpaceBookings.length) {
      const conflictingBookings = new Map<number, Array<ConflictingSpaceBooking>>();
      reservationInput.conflictingSpaceBookings.forEach((item) => {
        conflictingBookings.set(item.id, reservationInput.conflictingSpaceBookings.filter(booking => booking.id == item.id));
      });
      reservationInput.conflictingSpaceBookings = [];
      conflictingBookings.forEach((value) => {
        value.sort((a, b) => {
          if (a.releaseType == ResolveConflictsChoise.ReleaseEntire) {
            return -1;
          }
          else if (a.releaseType == ResolveConflictsChoise.ReleaseConflicting) {
            if (b.releaseType == ResolveConflictsChoise.Keep) {
              return -1;
            }
            else if (b.releaseType == ResolveConflictsChoise.ReleaseEntire) {
              return 1;
 }
            else {
              return 0;
 }
          }
          else {
            return 1;
 }
        });
        reservationInput.conflictingSpaceBookings.push(value[0]);
      });
    }

    return this.dataContextService.postData(url, reservationInput, true, false, isDelegate).pipe(map(res => {
      this.spinnerService.stop();
      if (res.status == 200) {
        const response: CreateReservationResponse = {
          id: res.data.id,
          revision: res.data.revision,
          upcomingBooking: res.data.upcomingBooking
        };
        return response;
      }
      else {
        if (res.status == 428) {
          const spaceNames = [];
          if (res.data.spaces) {
            if (res.data.message) {
              res.data.message = res.data.message.replace('.', '');
            }
            res.data.spaces.forEach(element => {
              spaceNames.push(element.spaceName);
            });
            res.data.message = res.data.message + ': ' + spaceNames.toString();
          }
        }
        return this.dataContextService.showErrorToaster(res);
      }
    }));
  }

  verify(reservationCard: ReservationDetailsFormValue, params: SpaceAvailabilityItem, bookingsCard: LocationItem) {

    const payload = {
      spaceBooking: {
        spaceId: params.space.id,
        name: params.space.name,
        attendeesCount: bookingsCard.attendees,
        eventTime: {
          setupTime: params.space.setupTime,
          tearDownTime: params.space.tearDownTime,
          timeSlotType: this.availabilitySearchParameter ? this.availabilitySearchParameter.eventTime.timeSlotType : TimeSlotType.SingleDay,
          singleDayEventData: null,
          recurrentEventData: null
        }
      }
    };

    if (reservationCard.reservationDetails.searchType == ReservationType.Standard) {
      payload.spaceBooking.eventTime.singleDayEventData = {
        startDate: params.suggestedAvailableTimeSlot.startDate,
        endDate: params.suggestedAvailableTimeSlot.endDate
      };
    }

    else if (reservationCard.reservationDetails.searchType == ReservationType.MultiDayRecurring) {
      const tzOffset = moment.tz(new Date(), bookingsCard.building.timeZoneIanaName).format('Z');
      payload.spaceBooking.eventTime.recurrentEventData = {
        recurrenceRule: {},
      };

      payload.spaceBooking.eventTime.recurrentEventData.recurrenceRule = {
        recurrencePatternType: RecurrencePatternTypes[reservationCard.reservationDetails.recurring.repeatEvery.pattern],
        interval: RecurrencePatternTypes.Daily
          ? reservationCard.reservationDetails.recurring.repeatEvery.days
          : reservationCard.reservationDetails.recurring.repeatEvery.weeks,
        startDate: `${moment(bookingsCard.startDate).format('YYYY-MM-DD')}T${moment(bookingsCard.startTime).format('HH:mm:00')}${tzOffset}`,
      };

      if (reservationCard.reservationDetails.recurring.endAfter.type === EndAfterType.Occurrences) {
        payload.spaceBooking.eventTime.recurrentEventData.recurrenceRule.endDate = `${moment(bookingsCard.endDate).format('YYYY-MM-DD')}T${moment(bookingsCard.endTime).format('HH:mm:00')}${tzOffset}`;
        payload.spaceBooking.eventTime.recurrentEventData.recurrenceRule.occurrences =
          reservationCard.reservationDetails.recurring.endAfter.occurrences;
      } else {
        payload.spaceBooking.eventTime.recurrentEventData.recurrenceRule.endDate = `${moment(
          reservationCard.reservationDetails.recurring.endAfter.date
        ).format('YYYY-MM-DD')}T${moment(bookingsCard.endTime).format('HH:mm:00')}${tzOffset}`;
      }

      if (reservationCard.reservationDetails.recurring.repeatEvery.pattern !== RecurrencePatternTypes.Daily) {
        payload.spaceBooking.eventTime.recurrentEventData.recurrenceRule.weeklyRecurrencePattern = {
          daysOfWeek: this.getRecurrenceWeeklyDaysOfWeek(reservationCard.reservationDetails.recurring.repeatEvery.weekDays),
        };
      }

    }

    return this.dataContextService.postData(`Users/${reservationCard.reservationDetails.owner[0].id}/ReservationRules/Verify`, this.dataContextService.removeNullValuesFromObject(payload)).pipe(map((res) => {
      return res.status === 200 ? res.data.reservationRuleResults : [];
    }));
  }

  getRecurrenceWeeklyDaysOfWeek(selectedDays: { [key: string]: boolean }): string[] {
    return WEEK_DAYS.filter((_, i) => selectedDays[i]).map((d) => d.fullName);
  }

  checkForConflicts(
    reservationDetails: ReservationDetailsFormValue,
    location: LocationItem,
    spaceAvailabilityItem: SpaceAvailabilityItem
  ) {
    return this.verify(reservationDetails, spaceAvailabilityItem, location).pipe(
      map((conflicts) => {
        return conflicts.filter((c) => c.type === ReservationVerifyType.MaxConcurrentIndividualBookings);
      })
    );
  }

  getFloorPlanData(buildingId: number, floorId: number) {
    const buildingData = DataContextService.Buildings.filter((building) => building.id === buildingId);
    const floor = buildingData[0].floors.find((floor) => floor.uniqueKey === floorId);
    const mapUrl = floor && floor.mapUrl; // getting mapUrl from Buildings masterdata
    const amenities = floor && floor.amenities;
    const uniqueAmenities = amenities.length > 0 ? Array.from(new Map(amenities.map((item) => [item.id, item])).values()) : []; // getting the unique amenities as floor can have multiple same amenities
    if (mapUrl) {
      return forkJoin({
        mapImage: this.dataContextService.getImage(mapUrl).pipe(
          map((res) => this.dataContextService.setDataToImageBody(this.dataContextService.arrayBufferToBase64(res)))
        ),
        amenities: this.getAmentiesIcons(uniqueAmenities).pipe(
          map(res => {
            const cachedData = res;
            return amenities.map(a => {
              const amenityFromRes = cachedData.find((amenity) => amenity.id == a.id);
              return amenityFromRes ? { ...a, iconLinks: amenityFromRes.iconLinks } : { ...a };
            }) as FloorAmenity[];
          })
        )
      }).pipe(
        map(res => res as FloorPlan)
      );
    }
    else {
      const floorPlan: FloorPlan = {};
      return of(floorPlan);
    }
  }

  private getAmentiesIcons(amenities: FloorAmenity[]): Observable<Amenity[]> {
    if (amenities.length > 0) {
      this.amenityCacheData = this.dataContextService.fetchFromLocalStorage('Amenities') ? JSON.parse(this.dataContextService.fetchFromLocalStorage('Amenities')) as AmenitiesCacheData : null; // fetching amenities from localStorage
      // looping thorugh the amenities
      const floorAmenities = amenities.map((a) => {
        const amenity = DataContextService.Amenities.find((item) => item.id === a.id); // finding the amenitiy from Master data for Icon Links
        if (!this.isCacheExpired) { // to check whether the last updated time is expired or not (when lastUpdated + expirationTime(stored in config.json) < currentDate)
          const amenityFromCache = this.amenityCacheData.Amenities.find((item) => item.id === a.id);
          if (amenityFromCache) {// if amenity is present in cache
            return of(amenityFromCache);
          }
          else { // new amenity not present in cache , we are calling API to fetch Icons
            return this.getAmenity(amenity, a);
          }
        }
        else { // if time is expired we are fetching icons for all
          return this.getAmenity(amenity, a);
        }
      });
      return forkJoin(floorAmenities);
    }
    return of([]);
  }

  private getAmenity(amenity: Amenity, floorAmenity: FloorAmenity) {
    return forkJoin({
      bigFilled: this.dataContextService.getImage(amenity.iconLinks.bigFilled, true),
      smallLine: this.dataContextService.getImage(amenity.iconLinks.smallLine, true )
    }).pipe(
      map((res) => {
        amenity = {
          ...amenity,
          iconLinks: {
            ...amenity.iconLinks,
            bigFilled: this.dataContextService.arrayBufferToBase64(res.bigFilled),
            smallLine: this.dataContextService.arrayBufferToBase64(res.smallLine)
          }
        };
        if ((this.amenityCacheData.Amenities.filter((cacheAmenity) => cacheAmenity.id === amenity.id)).length == 0) {
          this.amenityCacheData.Amenities.push(amenity);
        }
        this.amenityCacheData.LastUpdated = moment(new Date()).format('YYYY-MM-DDTHH:mm:00');
        this.dataContextService.storeInLocalStorage('Amenities', this.amenityCacheData);
        return amenity;
      }));
  }


createWaitlist(waitListRequestObject: WaitListCreationRequest, OnBehalfOf) {
  const url = `Users/${OnBehalfOf}/WorkspaceWaitLists`;
  return this.dataContextService.postData(url, waitListRequestObject, true).pipe(map((res) => {
    if (res.status == 200) {
      return res;
    } else {
      this.dataContextService.showErrorToaster(res);
    }
  }));
  }

  getReservationOwners(emails: string[]): Observable<UserByEmail[]> {
    return this.userService.getUsersByEmail(emails);
  }
  mapDateToApiFormat(date: Date, tz?: TimeZone, timeZoneAbbreviation?: string) {
    const mDate = moment(date);
    if (!tz) {
      tz = DataContextService.TimeZones.find(tz => tz.abbreviation === timeZoneAbbreviation);
    }
    if (tz.offset.includes('-', 0)) {
      return `${mDate.format('YYYY-MM-DD')}T${mDate.format('HH:mm:00')}${tz.offset.slice(0, -3)}`;
    }
    else {
      return `${mDate.format('YYYY-MM-DD')}T${mDate.format('HH:mm:00')}${'+' + tz.offset.slice(0, -3)}`;
    }
  }
}

// check on usage
export class ReservationTask {
  constructor(
    public referenceId: string,
    public referenceType: string,
    public notes: string,
    public instructions: string,
    public dueDate: string,
    public taskTypeId: number = 8,
    public assignedTo: number = 0,
    public status: number = 1
  ) { }
}
